import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/useSeo";
import { toast } from "sonner";
import { Search, BadgeCheck, AlertCircle, Sparkles, Leaf, Plus, Bookmark, Mountain, Network, Hexagon, List, Circle } from "lucide-react";
import LineageSubmitModal from "../components/LineageSubmitModal";
import ClassificationChip from "../components/ClassificationChip";
import { CLASSIFICATION_FILTER_OPTIONS } from "../lib/classification";
import GeneticsMap from "../components/lineage/GeneticsMap";
import TraitMatrix from "../components/lineage/TraitMatrix";
import ArchiveSections from "../components/lineage/ArchiveSections";
import LineageList from "../components/lineage/LineageList";
import StrainProofStats, { StrainProofStatsProvider } from "../components/StrainProofStats";
import ForgeIndexChip, { ForgeIndexBatchProvider } from "../components/ForgeIndexChip";
import ToolGateway from "../components/ToolGateway";

// v3.98 — Reverted to the original 4-tab layout. The "More views" dropdown
// was unnecessary friction; the four genetics views are first-class.
const TABS = [
  { id: "archive", label: "Explore",        Icon: Leaf,     testid: "lineage-tab-archive" },
  { id: "map",     label: "Genetics Map",   Icon: Network,  testid: "lineage-tab-map" },
  { id: "matrix",  label: "Trait Matrix",   Icon: Hexagon,  testid: "lineage-tab-matrix" },
  { id: "list",    label: "List view",      Icon: List,     testid: "lineage-tab-list" },
];

/**
 * v3.79 — Lineage Atlas with public premium tabs:
 *   • Strain Archive — searchable record list (the prior /lineage experience)
 *   • Cannabis Genetics Map — SVG genetics graph derived from approved lineage
 *   • Trait Matrix — aggregated cultivar metadata (no fake genomic claims)
 *
 * Admins / breeder records that aren't `_LINEAGE_PUBLIC_STATUSES` never reach
 * the map or matrix — the backend `/lineage/graph` and `/lineage/traits`
 * endpoints already restrict to publicly approved records.
 */
export default function LineageAtlas() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  // v3.99v — Default landing tab flipped from "archive" → "map". The Genetics
  // Map is the headline feature collectors come here for; surfacing it first
  // matches user intent and reduces clicks for the most-used view.
  const initialTab = TABS.find((t) => t.id === tabParam) ? tabParam : "map";
  const [tab, setTab] = useState(initialTab);
  const [items, setItems] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [verification, setVerification] = useState("");
  const [seedType, setSeedType] = useState("");
  const [classification, setClassification] = useState([]);  // array of values
  const [landrace, setLandrace] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  // v3.80 — archive defaults to the sectioned browse view. Users can flip to
  // the full searchable archive when they actually need to query.
  const [searchOpen, setSearchOpen] = useState(false);

  useSeo({
    title: "Cannabis Genetics Map | CannaForge",
    description: "Explore cannabis strain lineage, parentage, families, breeders, landraces, and available genetics through the CannaForge interactive genetics map.",
    canonical: "/lineage",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "CannaForge", "item": "https://thecannaforge.com/" },
          { "@type": "ListItem", "position": 2, "name": "Lineage Atlas", "item": "https://thecannaforge.com/lineage" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Cannabis Genetics Map",
        "url": "https://thecannaforge.com/lineage",
        "description": "Interactive cannabis genetics map. Pedigree visualisation across 900+ canonical strains, breeder lineage, family connections, and live CannaForge listings.",
        "isPartOf": { "@type": "WebSite", "name": "CannaForge", "url": "https://thecannaforge.com" },
      },
    ],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 60 };
      if (q) params.q = q;
      if (verification) params.verification_status = verification;
      if (seedType) params.seed_type = seedType;
      if (landrace) params.landrace = true;
      const r = await api.get("/lineage", { params });
      setItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, verification, seedType, landrace]);

  useEffect(() => { if (tab === "archive") load(); }, [load, tab]);
  useEffect(() => {
    api.get("/lineage/featured", { params: { limit: 4 } }).then(r => setFeatured(r.data || [])).catch(() => {});
  }, []);

  // v3.85 — when a user lands here from a "Tap to suggest" stub click on the
  // Genetics Map, auto-open the submit modal with the name prefilled and
  // strip the query so the modal doesn't keep popping back.
  const suggestName = searchParams.get("suggest");
  useEffect(() => {
    if (!suggestName) return;
    setSubmitOpen(true);
  }, [suggestName]);

  const switchTab = (id) => {
    setTab(id);
    const next = new URLSearchParams(searchParams);
    // v3.99v — "map" is now the default landing tab, so it's the one that
    // drops the `?tab=` param (clean URL). "archive" stays in the URL when
    // selected so refresh / share preserves the user's choice.
    if (id === "map") next.delete("tab"); else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  // v3.96 — Live stat cells on the hero (right column). Tiny endpoint that
  // already exists (`/lineage/featured` for save_count) plus the graph
  // count gives us "Verified records · Active breeders · Most-saved today"
  // without a new API. Falls back gracefully when any fetch errors.
  const [heroStats, setHeroStats] = useState({ verified: null, breeders: null, top: null });
  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get("/lineage/graph", { params: { limit: 500 } }).then(r => r.data).catch(() => null),
      api.get("/lineage/breeders-directory").then(r => r.data).catch(() => null),
      api.get("/lineage/featured", { params: { limit: 1 } }).then(r => r.data).catch(() => null),
    ]).then(([graph, dir, feat]) => {
      if (cancel) return;
      setHeroStats({
        verified: graph?.total_public_records ?? null,
        breeders: (dir?.vendors?.length ?? 0) + (dir?.originators?.length ?? 0) || null,
        top: feat?.[0] || null,
      });
    });
    return () => { cancel = true; };
  }, []);

  const canSubmit = !!user;
  const isLogged = !!user;

  return (
    <div data-testid="lineage-atlas-page">
      {/* v3.96 — Split hero. Left = tight pitch + CTA; Right = live stat
          cells. Total vertical ~36vh (was 60vh+). Removed the purple-ish
          radial gradient blobs — kept a thin emerald rule and a subtle
          gold dot pattern on the right column for editorial texture. */}
      <section className="relative bg-[var(--bg)] border-b border-[var(--border)] overflow-hidden"
               data-testid="lineage-hero">
        <div className="absolute inset-y-0 right-0 w-1/2 opacity-[0.04] pointer-events-none hidden md:block"
             style={{
               backgroundImage:"radial-gradient(circle, var(--gold) 1px, transparent 1.5px)",
               backgroundSize: "18px 18px",
             }}/>
        <div className="max-w-[1400px] mx-auto px-6 py-10 md:py-12 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 relative">
          {/* Left: pitch */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="label-eyebrow mb-3 flex items-center gap-2" style={{color:"var(--gold)"}}>
              <Leaf className="w-3 h-3"/> Lineage Atlas
              <span className="inline-flex items-center gap-1.5 ml-2"
                    style={{ color: "var(--primary-text)" }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse"
                      style={{ background: "var(--primary-text)" }}/>
                Live archive
              </span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] mb-4"
                style={{color:"var(--text)"}}>
              The cannabis<br/>
              <span style={{color:"var(--primary-text)"}}>genetics map.</span>
            </h1>
            <p className="max-w-xl text-sm md:text-base leading-relaxed mb-5" style={{color:"var(--text-2)"}}>
              Verified lineage, parentage, originators, and landrace records. Contributed by
              breeders and collectors — always evolving.
            </p>
            <div className="flex flex-wrap gap-2">
              {canSubmit && (
                <button onClick={() => setSubmitOpen(true)}
                        className="btn-primary inline-flex items-center gap-2"
                        data-testid="lineage-submit-cta">
                  <Plus className="w-4 h-4"/> Contribute
                </button>
              )}
              {!isLogged && (
                <Link to="/register" className="btn-primary inline-flex items-center gap-2">
                  <Plus className="w-4 h-4"/> Join to contribute
                </Link>
              )}
              <Link to="/breeders" className="btn-outline">Breeders</Link>
            </div>
          </div>
          {/* Right: live stat cells */}
          <div className="lg:col-span-5 grid grid-cols-3 gap-2 lg:gap-3 self-center"
               data-testid="lineage-hero-stats">
            <HeroStat
              label="Verified records"
              value={heroStats.verified}
              accent="var(--primary-text)"
              onClick={() => switchTab("archive")}
              testid="hero-stat-verified" />
            <HeroStat
              label="Breeders contributing"
              value={heroStats.breeders}
              accent="var(--gold)"
              onClick={() => { /* future: /breeders/contributing */ }}
              testid="hero-stat-breeders" />
            <HeroStat
              label="Most-saved today"
              value={heroStats.top?.strain_name}
              isText
              accent="var(--text)"
              onClick={() => heroStats.top && (window.location.href = `/strains/${heroStats.top.slug || heroStats.top.id}`)}
              testid="hero-stat-top" />
          </div>
        </div>
      </section>

      {/* v3.99v — Tab bar redesigned for clear "I am a tab" affordance,
          especially on mobile where the previous text-only underline was
          easily missed. Now: pill-shaped chips with a clear border,
          background, and bold gold active state. Active tab is also lifted
          via a heavier border weight + filled background so it reads as
          a button group rather than text. */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 mt-6 md:mt-8" data-testid="lineage-tab-bar">
        <div role="tablist" aria-label="Lineage Atlas tabs"
             className="flex items-stretch gap-1.5 sm:gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button key={t.id}
                      role="tab"
                      aria-selected={active}
                      onClick={() => switchTab(t.id)}
                      className="relative px-3 sm:px-4 md:px-5 py-2.5 sm:py-3 text-[11px] sm:text-xs md:text-sm font-mono uppercase tracking-[0.16em] sm:tracking-[0.18em] whitespace-nowrap inline-flex items-center gap-1.5 sm:gap-2 border transition-all snap-start"
                      style={{
                        color: active ? "var(--bg)" : "var(--text-2)",
                        background: active ? "var(--gold)" : "var(--surface)",
                        borderColor: active ? "var(--gold)" : "var(--border-strong)",
                        borderWidth: active ? 1 : 1,
                        borderRadius: 2,
                        boxShadow: active ? "0 0 0 2px rgba(212,175,55,0.18)" : "none",
                      }}
                      data-testid={t.testid}>
                <t.Icon className="w-3.5 h-3.5"
                        style={{ color: active ? "var(--bg)" : "var(--text-muted)" }} />
                {t.label}
              </button>
            );
          })}
        </div>
        <div className="hidden md:block text-[10px] font-mono uppercase tracking-[0.18em] mt-2 text-right"
             style={{ color: "var(--text-muted)" }}>
          Public · approved records only
        </div>
      </section>

      {/* GENETICS MAP TAB */}
      {tab === "map" && (
        <section className="max-w-[1400px] mx-auto px-6 mt-6" data-testid="lineage-tab-content-map">
          <p className="text-sm leading-relaxed max-w-2xl mb-4" style={{ color: "var(--text-2)" }}>
            A live, clickable graph of approved cannabis lineage. Each node is a public strain record — click any
            node to open its lineage page, explore parents and descendants, and see what's currently listed on
            CannaForge.
          </p>
          {user ? (
            <GeneticsMap centerSlug={searchParams.get("center") || undefined} />
          ) : (
            <ToolGateway
              testid="lineage-map-gateway"
              eyebrow="Member tool · Genetics Map"
              title="Unlock the full Cannabis Genetics Map"
              description="Create a free account to drag, zoom, search, and focus the interactive lineage graph. See parents, children, siblings, family clusters, and breeder-connected genetics across 900+ canonical strains."
            />
          )}

          {/* v3.99c.41 — Crawlable strain index. Below the interactive map
              we render an HTML link list of the most important public
              strains so search-engine crawlers (and screen readers, and
              users with JS disabled) can still discover canonical staples,
              listing-connected strains, and family anchors without
              interacting with the SVG graph. This is the SEO surface — the
              map above is the user-facing exploration tool. */}
          <CrawlableStrainIndex />
        </section>
      )}

      {/* v3.89e — LIST TAB. The map is the wow-factor visualization; the
          list is the workhorse for actually browsing 1K+ records with
          search / filter / sort / pagination. */}
      {tab === "list" && (
        <section className="max-w-[1400px] mx-auto px-6 mt-6" data-testid="lineage-tab-content-list">
          <p className="text-sm leading-relaxed max-w-2xl mb-4" style={{ color: "var(--text-2)" }}>
            Browse every approved lineage record in a sortable, filterable list. Search by strain name, parent
            strain, or alias. Filter by classification, seed type, or whether the strain has a live drop on
            CannaForge. Click a row to open its lineage page.
          </p>
          <LineageList />
        </section>
      )}

      {/* TRAIT MATRIX TAB */}
      {tab === "matrix" && (
        <section className="max-w-[1400px] mx-auto px-6 mt-6" data-testid="lineage-tab-content-matrix">
          <p className="text-sm leading-relaxed max-w-2xl mb-4" style={{ color: "var(--text-2)" }}>
            Structured cultivar metadata across the approved lineage set: classification spectrum, strain
            families, seed and cycle types, preservation flags, and the top recorded traits. Cultivar metadata
            only — no claims about lab sequencing.
          </p>
          {user ? (
            <TraitMatrix />
          ) : (
            <ToolGateway
              testid="lineage-matrix-gateway"
              eyebrow="Member tool · Trait Matrix"
              title="Unlock the full Trait Matrix"
              description="Create a free account to combine family, terpene, aroma, classification, and seed-type filters across the public lineage set. Build queries like 'every citrus-leaning Cookies cross in feminized form' and surface the strains and breeders behind them."
            />
          )}
        </section>
      )}

      {/* STRAIN ARCHIVE TAB */}
      {tab === "archive" && (
      <>
      {!searchOpen ? (
        // v3.80 — Sectioned browse landing.
        <section className="max-w-[1400px] mx-auto px-6 mt-8 md:mt-10 mb-12"
                 data-testid="lineage-archive-landing">
          <ArchiveSections onJumpToSearch={(query, filters) => {
            setSearchOpen(true);
            if (query) setQ(query);
            if (filters?.classification) setClassification([filters.classification]);
            if (filters?.preservation === "landrace") setLandrace(true);
            setTimeout(() => {
              document.querySelector('[data-testid="lineage-search-input"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 80);
          }}/>
        </section>
      ) : (
      <>
      {/* v3.99ae — Provider preloads grow-log stats for every strain
          rendered on the page (both featured + search list) in ONE batch
          request, so each LineageCard renders its proof line without an
          additional round-trip.
          v3.99av — same pattern for Forge Index batch. */}
      <StrainProofStatsProvider strainIds={[...featured, ...items].map((l) => l.id)}>
      <ForgeIndexBatchProvider scope="strain" keys={[...featured, ...items].map((l) => l.slug || l.id)}>
      {/* Featured strip (when in search mode) */}
      {featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 mt-10" data-testid="lineage-featured">
          <h2 className="font-display text-2xl md:text-3xl tracking-tighter mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{color:"var(--gold)"}}/> Featured lineages
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {featured.map(l => <LineageCard key={l.id} item={l} featured/>)}
          </div>
        </section>
      )}

      {/* Search + filters */}
      <section className="max-w-[1400px] mx-auto px-6 mt-12">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setSearchOpen(false)}
                  className="text-xs font-mono uppercase tracking-[0.16em] underline"
                  style={{color:"var(--text-muted)"}}
                  data-testid="lineage-archive-back-to-browse">
            ← Back to browse
          </button>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--border)] p-4 md:p-5 mb-5 flex flex-wrap items-center gap-2">
          <label className="flex-1 min-w-[240px] flex items-center gap-2 border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2">
            <Search className="w-4 h-4 shrink-0" style={{color:"var(--text-muted)"}}/>
            <input value={q} onChange={(e)=>setQ(e.target.value)}
                   placeholder="Search by strain name, alias, tag…"
                   className="bg-transparent border-0 outline-none flex-1 text-sm"
                   style={{color:"var(--text)"}}
                   data-testid="lineage-search-input"/>
          </label>
          <select value={verification} onChange={(e)=>setVerification(e.target.value)}
                  className="input-field !py-2 text-xs"
                  data-testid="lineage-filter-verification">
            <option value="">All statuses</option>
            <option value="verified">Verified</option>
            <option value="breeder_submitted">Breeder-submitted</option>
            <option value="community_submitted">Collector-submitted</option>
            <option value="disputed">Disputed</option>
          </select>
          <select value={seedType} onChange={(e)=>setSeedType(e.target.value)}
                  className="input-field !py-2 text-xs"
                  data-testid="lineage-filter-seedtype">
            <option value="">All seed types</option>
            <option value="regular">Regular</option>
            <option value="feminized">Feminized</option>
            <option value="autoflower">Autoflower</option>
            <option value="clone_only">Clone only</option>
          </select>
          <button onClick={() => setLandrace(x => !x)}
                  className={`px-3 py-2 border text-xs inline-flex items-center gap-1.5 ${landrace ? "border-[var(--gold)] text-[var(--gold)]" : "border-[var(--border)]"}`}
                  style={{color: landrace ? "var(--gold)" : "var(--text-2)"}}
                  data-testid="lineage-filter-landrace">
            <Mountain className="w-3 h-3"/> Landrace
          </button>
        </div>

        {/* Classification filter row */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="label-eyebrow" style={{color:"var(--text-muted)"}}>Classification</span>
          {CLASSIFICATION_FILTER_OPTIONS.map(opt => {
            const active = classification.includes(opt.value);
            return (
              <button key={opt.value}
                      onClick={() => setClassification(prev =>
                        prev.includes(opt.value) ? prev.filter(v => v !== opt.value) : [...prev, opt.value]
                      )}
                      className={`px-3 py-1.5 border text-xs font-mono uppercase tracking-[0.16em] ${active ? "border-[var(--gold)] text-[var(--gold)]" : "border-[var(--border)] text-[var(--text-2)]"}`}
                      data-testid={`lineage-filter-classification-${opt.value}`}>
                {opt.label}
              </button>
            );
          })}
          {classification.length > 0 && (
            <button onClick={() => setClassification([])}
                    className="text-xs underline" style={{color:"var(--text-muted)"}}
                    data-testid="lineage-filter-classification-clear">
              Clear
            </button>
          )}
        </div>

        <div className="flex items-center justify-between mb-3 text-xs" style={{color:"var(--text-muted)"}}>
          <span>{loading ? "Loading…" : `${items.length} record${items.length === 1 ? "" : "s"}`}</span>
          <span className="font-mono">verified · breeder-submitted · collector-submitted · disputed</span>
        </div>

        {items.length === 0 && !loading ? (
          <div className="bg-[var(--surface)] border border-[var(--border)] p-10 text-center text-sm" style={{color:"var(--text-muted)"}}>
            <Leaf className="w-6 h-6 mx-auto mb-3" style={{color:"var(--gold)"}}/>
            Building the archive. {canSubmit ? "Help us start it — submit the first lineage record." : "Join to contribute."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {items.map(l => <LineageCard key={l.id} item={l}/>)}
          </div>
        )}
      </section>
      </ForgeIndexBatchProvider>
      </StrainProofStatsProvider>
      </>
      )}
      </>
      )}

      {submitOpen && (
        <LineageSubmitModal onClose={() => {
                              setSubmitOpen(false);
                              if (suggestName) {
                                const next = new URLSearchParams(searchParams);
                                next.delete("suggest");
                                setSearchParams(next, { replace: true });
                              }
                            }}
                            prefillStrainName={suggestName || ""}
                            onSubmitted={() => {
                              setSubmitOpen(false);
                              if (suggestName) {
                                const next = new URLSearchParams(searchParams);
                                next.delete("suggest");
                                setSearchParams(next, { replace: true });
                              }
                              load();
                            }}/>
      )}
    </div>
  );
}

export function LineageCard({ item, featured }) {
  // v3.96 — Editorial dense tile. Removed image-first chunky layout in favor
  // of a typographic, info-rich card: strain name (serif display) over
  // mono `P1 × P2` parentage, breeder credit + verified pill + save count
  // on the meta row, and an emerald "On CannaForge" live-dot when the
  // record has matching listings (driven by `has_listings`).
  const status = (item.verification_status || "").toLowerCase();
  const statusMeta = LINEAGE_STATUS[status] || { label: status.toUpperCase(), color: "var(--text-2)", Icon: AlertCircle };
  const isPinned = !!item.is_featured;
  const onCannaForge = !!item.has_listings;
  return (
    <Link to={`/strains/${item.slug || item.id}`}
          className={`group block relative bg-[var(--surface)] border ${(featured || isPinned) ? "border-[var(--gold)]/50" : "border-[var(--border)]"} hover:border-[var(--gold)] p-4 transition-colors`}
          style={{ borderRadius: 2 }}
          data-testid={`lineage-card-${item.id}`}>
      {/* Live-listing dot — emerald + soft pulse, top-right corner. */}
      {onCannaForge && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-1.5 py-0.5 border font-mono text-[9px] uppercase tracking-[0.16em]"
              style={{ borderColor: "var(--primary-text)", color: "var(--primary-text)", borderRadius: 2 }}
              title="Live on CannaForge"
              data-testid={`lineage-card-onsite-${item.id}`}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: "var(--primary-text)" }}/>
          On CannaForge
        </span>
      )}
      {isPinned && !onCannaForge && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] border"
              style={{ color: "var(--gold)", borderColor: "var(--gold)", borderRadius: 2 }}
              title="Editorially featured by CannaForge"
              data-testid={`lineage-card-featured-${item.id}`}>
          <Sparkles className="w-2.5 h-2.5"/> Featured
        </span>
      )}

      {/* Strain name */}
      <h3 className="font-serif text-lg md:text-xl leading-tight pr-24 mb-1.5"
          style={{ color:"var(--text)" }}>
        {item.strain_name}
      </h3>

      {/* Parents inline — the most-scanned info on a card */}
      <div className="text-xs font-mono mb-3" style={{ color: "var(--text-muted)" }}>
        {(item.parent_one || item.parent_two) ? (
          <>
            {item.parent_one || "?"} <span style={{ color: "var(--gold)" }}>×</span> {item.parent_two || "?"}
          </>
        ) : item.landrace || item.heirloom ? (
          <span style={{ color: "var(--gold)" }}>{item.landrace ? "Landrace" : "Heirloom"}</span>
        ) : (
          <span style={{ color: "var(--text-muted)", opacity: 0.6 }}>Parentage unknown</span>
        )}
      </div>

      {/* Classification chip (compact) */}
      {item.classification && (
        <div className="mb-3">
          <ClassificationChip value={item.classification} short size="sm"
                              testId={`lineage-card-classification-${item.id}`}/>
        </div>
      )}

      {/* v3.99ae — Compact proof-stats line (verified grows · herm reports).
          Renders nothing when the strain has zero grow logs so cards without
          evidence don't show an empty placeholder. */}
      <div className="mb-2 flex items-center gap-2 flex-wrap">
        <StrainProofStats strainIdOrSlug={item.id}
                          testId={`lineage-card-proof-stats-${item.id}`} />
        <ForgeIndexChip scope="strain" idOrSlug={item.slug || item.id}
                        testId={`lineage-card-forge-${item.id}`} />
      </div>

      {/* Notes preview — single line, no chunky paragraph */}
      {item.lineage_notes && (
        <p className="text-[12px] line-clamp-1 leading-relaxed mb-3" style={{ color: "var(--text-2)" }}>
          {item.lineage_notes}
        </p>
      )}

      {/* Meta row — verified pill on left, breeder credit center, save count right */}
      <div className="flex items-center gap-2 mt-auto pt-2 border-t border-[var(--border)] text-[10px] font-mono"
           style={{ color: "var(--text-muted)" }}>
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border"
              style={{ color: statusMeta.color, borderColor: statusMeta.color, borderRadius: 2 }}
              title={statusMeta.label}>
          <statusMeta.Icon className="w-2.5 h-2.5"/> {statusMeta.short}
        </span>
        {(item.originator_text || item.breeder_name) && (
          <span className="truncate min-w-0" title={item.originator_text || item.breeder_name}>
            {item.originator_text || item.breeder_name}
          </span>
        )}
        {(item.save_count || 0) > 0 && (
          <span className="ml-auto inline-flex items-center gap-1" style={{ color: "var(--gold)" }}>
            <Bookmark className="w-2.5 h-2.5"/> {item.save_count}
          </span>
        )}
      </div>
    </Link>
  );
}

// v3.96 — Hero stat cell. Renders a labeled metric or a "Most-saved" text
// summary; gracefully handles null (loading) by showing a thin pulse.
function HeroStat({ label, value, accent, isText, onClick, testid }) {
  const isLoading = value === null || value === undefined;
  const Wrapper = onClick && !isLoading ? "button" : "div";
  return (
    <Wrapper
      onClick={onClick && !isLoading ? onClick : undefined}
      className={`group text-left bg-[var(--surface)] border border-[var(--border)] p-3 lg:p-4 ${onClick && !isLoading ? "hover:border-[var(--gold)] transition-colors cursor-pointer" : ""}`}
      style={{ borderRadius: 2 }}
      data-testid={testid}
    >
      <div className="font-mono text-[9px] uppercase tracking-[0.18em] mb-1.5"
           style={{ color: "var(--text-muted)" }}>
        {label}
      </div>
      {isLoading ? (
        <div className="h-7 w-12 bg-[var(--bg-2)] animate-pulse"/>
      ) : isText ? (
        <div className="font-serif text-base lg:text-lg leading-tight truncate"
             style={{ color: accent || "var(--text)" }}>
          {value}
        </div>
      ) : (
        <div className="font-display text-3xl lg:text-4xl tracking-tighter leading-none"
             style={{ color: accent || "var(--text)" }}>
          {value}
        </div>
      )}
    </Wrapper>
  );
}

export const LINEAGE_STATUS = {
  verified: { label: "Verified by CannaForge", short: "Verified", color: "var(--primary-text)", Icon: BadgeCheck },
  breeder_submitted: { label: "Breeder-submitted", short: "Breeder", color: "var(--gold)", Icon: Leaf },
  community_submitted: { label: "Collector-submitted", short: "Community", color: "#8FB3FF", Icon: Sparkles },
  disputed: { label: "Disputed by community", short: "Disputed", color: "var(--secondary)", Icon: AlertCircle },
  pending: { label: "Pending review", short: "Pending", color: "var(--text-muted)", Icon: AlertCircle },
  rejected: { label: "Rejected", short: "Rejected", color: "var(--text-muted)", Icon: AlertCircle },
  archived: { label: "Archived", short: "Archived", color: "var(--text-muted)", Icon: AlertCircle },
};



/**
 * CrawlableStrainIndex — v3.99c.41
 * SEO surface for the Genetics Map page. Rendered as plain HTML link
 * lists so search-engine crawlers and assistive tech can discover
 * canonical strains without interacting with the SVG graph above.
 *
 * Sections:
 *   - Available genetics (live CannaForge listings)
 *   - Canonical staples (priority_canonical strains)
 *   - Family anchors (top-saved strain per major family)
 *   - Foundation lines (landrace + heirloom + preservation)
 *
 * Each item is a normal /strains/:slug anchor so internal linking
 * boosts every strain's authority and gives Google something to crawl
 * even if the canvas/SVG fails to render.
 */
function CrawlableStrainIndex() {
  const [data, setData] = useState({ available: [], canon: [], anchors: [], foundation: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get("/lineage", { params: { has_listing: true, limit: 24 } }).then(r => r.data).catch(() => []),
      api.get("/lineage", { params: { priority_canonical: true, limit: 60 } }).then(r => r.data).catch(() => []),
      api.get("/lineage/featured", { params: { limit: 24 } }).then(r => r.data).catch(() => []),
      api.get("/lineage", { params: { landrace: true, limit: 24 } }).then(r => r.data).catch(() => []),
    ]).then(([avail, canon, anchors, foundation]) => {
      if (!alive) return;
      setData({
        available: avail || [],
        canon: canon || [],
        anchors: anchors || [],
        foundation: foundation || [],
      });
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  const empty = data.available.length + data.canon.length + data.anchors.length + data.foundation.length === 0;
  if (empty) return null;

  return (
    <div className="mt-10 border-t border-[var(--border)] pt-8 space-y-8"
         data-testid="lineage-crawlable-index"
         aria-label="Public strain index for search engines and screen readers">
      <p className="text-xs font-mono uppercase tracking-[0.18em]" style={{ color: "var(--text-muted)" }}>
        Strain index · Crawlable links · The map above is interactive — the lists below are for direct navigation.
      </p>

      {data.available.length > 0 && (
        <CrawlableSection
          eyebrow="Available on CannaForge"
          title="Shop live genetics"
          items={data.available}
          testid="lineage-crawlable-available"
        />
      )}
      {data.canon.length > 0 && (
        <CrawlableSection
          eyebrow="Canonical staples"
          title="Foundational cannabis strains"
          items={data.canon}
          testid="lineage-crawlable-canon"
        />
      )}
      {data.anchors.length > 0 && (
        <CrawlableSection
          eyebrow="Family anchors"
          title="Most-saved strains"
          items={data.anchors}
          testid="lineage-crawlable-anchors"
        />
      )}
      {data.foundation.length > 0 && (
        <CrawlableSection
          eyebrow="Foundation lines"
          title="Landrace, heirloom, preservation"
          items={data.foundation}
          testid="lineage-crawlable-foundation"
        />
      )}

      {/* Trait page cross-links — funnel crawlers into the family /
          terpene / classification SEO surfaces. */}
      <div data-testid="lineage-crawlable-traits">
        <div className="label-eyebrow mb-2" style={{ color: "var(--gold)" }}>
          Browse by trait
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["cookies", "og", "gelato", "diesel", "kush", "haze", "skunk", "runtz", "cake", "purple"].map((f) => (
            <Link key={`fam-${f}`} to={`/families/${f}`}
                  className="text-[11px] font-mono uppercase tracking-[0.14em] border border-[var(--border)] hover:border-[var(--gold)]/60 px-2 py-1 transition-colors"
                  style={{ color: "var(--text-2)", borderRadius: 2 }}
                  data-testid={`lineage-crawlable-family-${f}`}>
              {f} family
            </Link>
          ))}
          {["hybrid", "indica", "sativa"].map((c) => (
            <Link key={`cls-${c}`} to={`/classifications/${c}`}
                  className="text-[11px] font-mono uppercase tracking-[0.14em] border border-[var(--border)] hover:border-[var(--gold)]/60 px-2 py-1 transition-colors"
                  style={{ color: "var(--text-2)", borderRadius: 2 }}
                  data-testid={`lineage-crawlable-class-${c}`}>
              {c}
            </Link>
          ))}
          <Link to="/preservation/landrace"
                className="text-[11px] font-mono uppercase tracking-[0.14em] border border-[var(--border)] hover:border-[var(--gold)]/60 px-2 py-1 transition-colors"
                style={{ color: "var(--text-2)", borderRadius: 2 }}
                data-testid="lineage-crawlable-preservation-landrace">
            landrace
          </Link>
        </div>
      </div>
    </div>
  );
}

function CrawlableSection({ eyebrow, title, items, testid }) {
  return (
    <div data-testid={testid}>
      <div className="label-eyebrow mb-1" style={{ color: "var(--gold)" }}>{eyebrow}</div>
      <h3 className="font-display text-xl md:text-2xl tracking-tighter mb-3"
          style={{ color: "var(--text)" }}>
        {title}
      </h3>
      <ul className="flex flex-wrap gap-1.5">
        {items.slice(0, 36).map((s) => (
          <li key={s.id}>
            <Link to={`/strains/${s.slug || s.id}`}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-[var(--border)] hover:border-[var(--gold)]/60 hover:bg-[var(--surface)] transition-colors"
                  style={{ color: "var(--text)", borderRadius: 2 }}
                  data-testid={`${testid}-link-${s.id}`}>
              {s.strain_name}
              {s.has_listing && (
                <span className="text-[9px] font-mono uppercase tracking-[0.12em] ml-1"
                      style={{ color: "var(--gold)" }}>live</span>
              )}
              {s.priority_canonical && (
                <Sparkles className="w-2.5 h-2.5" style={{ color: "var(--gold)" }} />
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
